import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../../styles/pages/SitePages.css';

const aboutStats = [
  { value: '35+', label: 'years in the industry' },
  { value: '4.9/5', label: 'customer satisfaction' },
  { value: '100%', label: 'custom fit process' },
];

const servicePanels = {
  commercial: {
    tabLabel: 'Commercial services',
    intro:
      'Custom blinds, shades, and shutters for offices, properties, and other professional spaces.',
    lead:
      'Designed for polished spaces with durable finishes and practical light control.',
    body:
      'We help property teams and designers choose refined window treatments that are simple to specify and built to last.',
    highlights: [
      {
        title: 'Space planning support',
        text: 'Tailored solutions for offices, hospitality, and larger projects.',
      },
      {
        title: 'Performance materials',
        text: 'Durable finishes and hardware chosen for everyday use.',
      },
      {
        title: 'Project coordination',
        text: 'Clear communication and dependable scheduling.',
      },
    ],
  },
  residential: {
    tabLabel: 'Residential services',
    intro:
      'Custom window coverings that bring warmth, privacy, and balance to the home.',
    lead:
      'Designed for comfort, beauty, and energy efficiency in every room.',
    body:
      'We guide homeowners toward treatments that complement the room and control light with ease.',
    highlights: [
      {
        title: 'Light control & privacy',
        text: 'Options that make bedrooms and living spaces more comfortable.',
      },
      {
        title: 'Energy-efficient fabrics',
        text: 'Thoughtful materials that help improve comfort year-round.',
      },
      {
        title: 'Made in the USA',
        text: 'Custom treatments made with quality and craftsmanship.',
      },
    ],
  },
};

const testimonialCards = [
  {
    title: 'PROFESSIONAL SERVICE FROM START TO FINISH',
    body: 'Working with Sonny Blinds was a great experience from the first consultation to the final installation. The team was knowledgeable, punctual, and helped us choose the perfect window treatments for our home. Everything was installed flawlessly and the finished product looks amazing. We could not be happier with the results and would gladly recommend them to family and friends.',
    author: 'Michael R.',
  },
  {
    title: 'BEAUTIFUL PRODUCTS AND ATTENTION TO DETAIL',
    body: 'We were impressed by the variety of options available and the quality of the materials. Sonny Blinds took the time to explain the differences between products and helped us find something that matched both our style and budget. The attention to detail throughout the process really stood out. Our new shades completely transformed the look of our living room.',
    author: 'Sarah T.',
  },
  {
    title: 'EXCELLENT VALUE AND OUTSTANDING QUALITY',
    body: 'After comparing several companies, we chose Sonny Blinds because of their competitive pricing and reputation for quality. The entire process was smooth and professional. The blinds arrived on schedule, fit perfectly, and look fantastic. It is refreshing to work with a company that delivers exactly what they promise.',
    author: 'David L.',
  },
  {
    title: 'TRUSTWORTHY, FRIENDLY, AND RELIABLE',
    body: 'Finding a company you can trust with your home is important, and Sonny Blinds exceeded our expectations. Everyone we spoke with was friendly, responsive, and genuinely cared about making sure we were satisfied. The installation was quick, clean, and professional. We will definitely be returning for future projects.',
    author: 'Jennifer K.',
  },
  {
    title: 'CLEAR COMMUNICATION AND BEAUTIFUL RESULTS',
    body: 'From the estimate to the final install, the process was easy to follow and very well organized. Sonny Blinds communicated clearly, showed up on time, and delivered a result that looks polished and custom.',
    author: 'Amanda P.',
  },
  {
    title: 'A COMPANY THAT TREATS YOU WITH CARE',
    body: 'We appreciated how patient and helpful the team was while we chose materials for multiple rooms. The end result feels elegant and cohesive, and the quality is exactly what we hoped for.',
    author: 'Brian M.',
  },
];

function TestimonialSlider() {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [dragLimit, setDragLimit] = useState(0);

  useEffect(() => {
    const updateDragLimit = () => {
      const viewport = viewportRef.current;
      const track = trackRef.current;

      if (!viewport || !track) {
        return;
      }

      const nextLimit = Math.max(0, track.scrollWidth - viewport.clientWidth);
      setDragLimit(nextLimit);
    };

    updateDragLimit();
    window.addEventListener('resize', updateDragLimit);

    return () => {
      window.removeEventListener('resize', updateDragLimit);
    };
  }, []);

  return (
    <div className="testimonial-slider-viewport" ref={viewportRef}>
      <motion.div
        ref={trackRef}
        className="testimonial-slider-track"
        drag="x"
        dragConstraints={{ left: -dragLimit, right: 0 }}
        dragElastic={0.08}
        whileTap={{ cursor: 'grabbing' }}
      >
        {testimonialCards.map((card) => (
          <article key={card.author} className="testimonial-card">
            <p className="testimonial-card-kicker">Customer review</p>
            <h3>{card.title}</h3>
            <p className="testimonial-card-body">{card.body}</p>
            <p className="testimonial-card-author">— {card.author}</p>
          </article>
        ))}
      </motion.div>
    </div>
  );
}

function AboutUsPage() {
  const [activeService, setActiveService] = useState('commercial');
  const activePanel = servicePanels[activeService];

  return (
    <>
      <section className="about-route">
        <div className="about-route-shell">
          <div className="about-route-background" aria-hidden="true" />
          <div className="container about-route-grid">
            <div className="about-route-copy">
              <p className="about-route-eyebrow">Our story</p>
              <h1>About Sonny Blinds</h1>
              <p className="about-route-lead">
                We create tailored window treatments with a calm, elevated approach.
              </p>
              <p>
                From consultation to installation, we focus on clarity, craftsmanship, and simple
                guidance so every selection feels deliberate and refined.
              </p>

              <div className="about-route-actions">
                <Link to="/contact" className="btn btn-primary">Request consultation</Link>
                <Link to="/products" className="btn btn-secondary">Browse products</Link>
              </div>

              <div className="about-route-stats" aria-label="Company highlights">
                {aboutStats.map((stat) => (
                  <div key={stat.label} className="about-route-stat">
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <aside className="about-route-panel">
              <div className="about-route-panel-tabs" aria-label="Service focus">
                <button
                  type="button"
                  className={activeService === 'commercial' ? 'is-active' : ''}
                  onClick={() => setActiveService('commercial')}
                >
                  {servicePanels.commercial.tabLabel}
                </button>
                <button
                  type="button"
                  className={activeService === 'residential' ? 'is-active' : ''}
                  onClick={() => setActiveService('residential')}
                >
                  {servicePanels.residential.tabLabel}
                </button>
              </div>

              <div className="about-route-panel-copy">
                <p className="about-route-panel-intro">{activePanel.intro}</p>
                <h2>{activePanel.lead}</h2>
                <p>{activePanel.body}</p>

                <ul className="about-route-checklist">
                  {activePanel.highlights.map((item) => (
                    <li key={item.title}>
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="about-testimonials-section">
        <div className="container">
          <div className="section-header about-testimonials-header">
            <p className="route-eyebrow">What customers say</p>
            <h2>Elegant feedback from recent projects</h2>
          </div>

          <TestimonialSlider />
        </div>
      </section>
    </>
  );
}

export default AboutUsPage;
